import FilterDrawer from 'components/Assignments/FilterDrawer';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import Desktop from 'pages/StudentAssignments/desktop';
import useAssignmentChatSummaries from 'hooks/chat/useAssignmentChatSummaries';
import Mobile from 'pages/StudentAssignments/mobile';
import { PageContext } from 'pages/StudentAssignments/PageProvider';
import Tablet from 'pages/StudentAssignments/tablet';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import assignmentService from 'services/assignment';
import { Loading } from 'shell/components';
import { hasUnreadChat } from 'utils/chat';

const sortSubmissionsByCreatedOn = (submissions = []) => {
    return [...submissions].sort((left, right) => (left.created_on || 0) - (right.created_on || 0));
};

const PageLayout = () => {
    const { isLoading, assignments: allAssignments } = useApp();
    const { auth } = useAuth();
    const history = useHistory();
    const { device } = useViewport();

    const { filters, setFilters, resetFilters } = useContext(PageContext);

    const [batch, setBatch] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [submissionsByAssignmentId, setSubmissionsByAssignmentId] = useState(null);
    const { chatsByAssignmentId, setChatsByAssignmentId } = useAssignmentChatSummaries({
        courseId: auth?.course_id,
        ownerAccountId: auth?.id,
    });

    const getFilterCount = (filters) => {
        let count = 0;
        if (filters.sort !== 'none') count++;
        return count;
    };

    const onClick = ({ id }) => {
        history.push(`/assignments/${id}`);
    };

    const onClickGetFeedback = ({ id }) => {
        history.push(`/assignments/${id}`);
    };

    const onClickViewFeedback = ({ id }) => {
        history.push(`/assignments/${id}`);
    };

    const onClickInquire = ({ id }) => {
        const latestSubmission = submissionsByAssignmentId[id]?.[submissionsByAssignmentId[id].length - 1];
        if (!latestSubmission?.id) return;

        history.push(`/assignments/${id}/submissions/${latestSubmission.id}`, { openChat: true });
    };

    useEffect(() => {
        if (!allAssignments) return;
        const assignments = allAssignments?.filter(
            ({ ai_student_feedback_enabled, is_draft }) => ai_student_feedback_enabled && !is_draft
        );
        if (assignments?.length) {
            assignments.forEach(async ({ id }) => {
                const batch = await assignmentService.listAssignmentBatch(
                    id,
                    JSON.stringify({
                        is_finished: false,
                        owner_account_id: auth.id,
                    })
                );
                const feedback = await assignmentService.listAssignmentFeedback(
                    id,
                    JSON.stringify({ owner_account_id: auth.id })
                );
                const studentSubmissions = await assignmentService.listAssignmentSubmissions(
                    id,
                    JSON.stringify({
                        type: 'formative',
                        owner_account_id: auth.id,
                    })
                );
                setBatch((current) => ({ ...(current || {}), [id]: batch }));
                setFeedback((current) => ({ ...(current || {}), [id]: [...feedback, ...studentSubmissions] }));
            });
        } else {
            setBatch({});
            setFeedback({});
        }
    }, [allAssignments, auth.id]);

    useEffect(() => {
        if (!allAssignments) return;

        const assignments = allAssignments.filter(
            ({ ai_student_feedback_enabled, is_draft }) => ai_student_feedback_enabled && !is_draft
        );

        if (!assignments.length) {
            setChatsByAssignmentId({});
            setSubmissionsByAssignmentId({});
            return;
        }

        setSubmissionsByAssignmentId(null);

        let isMounted = true;

        const loadSubmissionData = async () => {
            try {
                const submissionsByAssignment = await Promise.all(
                    assignments.map(async ({ id }) => {
                        const nextStudentSubmissions = await assignmentService.listAssignmentSubmissions(
                            id,
                            JSON.stringify({
                                type: 'formative',
                                owner_account_id: auth.id,
                            })
                        );
                        return {
                            assignmentId: id,
                            submissions: sortSubmissionsByCreatedOn(nextStudentSubmissions),
                        };
                    })
                );

                if (!isMounted) return;

                setSubmissionsByAssignmentId(
                    submissionsByAssignment.reduce((result, item) => {
                        result[item.assignmentId] = item.submissions;
                        return result;
                    }, {})
                );
            } catch (error) {
                if (!isMounted) return;
                console.error(error);
            }
        };

        loadSubmissionData();

        return () => {
            isMounted = false;
        };
    }, [allAssignments, auth.id, setChatsByAssignmentId]);

    const props = {
        batch,
        feedback,
        chatsByAssignmentId,
        submissionsByAssignmentId,
        filters,
        setFilters,
        getFilterCount,
        onClick,
        onClickGetFeedback,
        onClickInquire,
        onClickViewFeedback,
        hasUnreadChatForAssignment: (assignmentId) =>
            hasUnreadChat({
                chat: chatsByAssignmentId[assignmentId],
                accountId: auth?.owner_account_id || auth?.id,
            }),
        shouldShowInquireButtonForAssignment: (assignmentId) => Boolean(chatsByAssignmentId[assignmentId]?.first_opened_on),
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    return isLoading || !feedback || !batch || !chatsByAssignmentId || !submissionsByAssignmentId ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <FilterDrawer filters={filters} setFilters={setFilters} resetFilters={resetFilters} />
        </>
    );
};

export default PageLayout;
